import React from 'react';
import { Box, ChevronRightIcon, Preloader } from '@common-fe/common-fe';

import { EMPTY_FIELD_VALUE } from '@/common/constants';

import { EllipsisText } from '../../../ActivityLog.styles';

import { CahngesetWrapper } from './Changeset.styles';
import useGetActivityLogChangesetQuery from './useGetActivityLogChangeset.query';

interface BodyProps {
  id: string;
  isTwoColumnMode?: boolean;
  type?: string;
}

const CREATE_KEY = 'CREATE';
const DELETE_KEY = 'DELETE';

const Body: React.FC<BodyProps> = ({ id, isTwoColumnMode, type }) => {
  const { isLoading, formattedData } = useGetActivityLogChangesetQuery(id);

  if (isLoading)
    return (
      <Box margin={{ top: 'spacing24' }}>
        <Preloader />
      </Box>
    );

  const showTwoColumns = isTwoColumnMode && (type === CREATE_KEY || type === DELETE_KEY);

  return (
    <CahngesetWrapper
      data-testid={`ActivityLog_changeset_${id}_rows`}
      margin={{ top: 'spacing8' }}
      background="canvas"
      border={{ size: '1px', color: 'border2' }}
      round="xsmall"
    >
      {formattedData.map((item) => {
        return (
          <Box
            key={item.id}
            data-testid={`ActivityLog_changeset_${id}_row_${item.id}`}
            direction="row"
            height="40px"
            align="center"
            pad={{ left: 'spacing12', right: 'spacing16' }}
          >
            <Box width="26%" data-testid={`ActivityLog_changeset_${id}_row_${item.id}_attribute`}>
              <EllipsisText
                title={item?.attribute}
                color="textSecondary"
                data-testid={`ActivityLog_changeset_${id}_row_${item.id}_attribute-value`}
              >
                {item?.attribute}
              </EllipsisText>
            </Box>
            {showTwoColumns ? (
              <Box
                width="37%"
                pad={{ left: 'spacing24' }}
                data-testid={`ActivityLog_changeset_${id}_row_${item.id}_value`}
              >
                {type === DELETE_KEY ? (
                  <EllipsisText
                    color="textBody"
                    hasUppercase={false}
                    data-testid={`ActivityLog_changeset_${id}_row_${item.id}_value-value`}
                    {...(item?.previous === EMPTY_FIELD_VALUE || !item?.previous
                      ? {}
                      : { title: item?.previous })}
                  >
                    {item?.previous || EMPTY_FIELD_VALUE}
                  </EllipsisText>
                ) : item?.updatedNode ? (
                  item.updatedNode
                ) : (
                  <EllipsisText
                    color="textBody"
                    hasUppercase={false}
                    weight="bold"
                    data-testid={`ActivityLog_changeset_${id}_row_${item.id}_value-value`}
                    {...(item?.updated === EMPTY_FIELD_VALUE || !item?.updated
                      ? {}
                      : { title: item?.updated })}
                  >
                    {item?.updated || EMPTY_FIELD_VALUE}
                  </EllipsisText>
                )}
              </Box>
            ) : (
              <>
                <Box
                  width="37%"
                  pad={{ left: 'spacing24' }}
                  data-testid={`ActivityLog_changeset_${id}_row_${item.id}_previous`}
                >
                  <EllipsisText
                    color="textBody"
                    hasUppercase={false}
                    data-testid={`ActivityLog_changeset_${id}_row_${item.id}_previous-value`}
                    {...(item?.previous === EMPTY_FIELD_VALUE || !item?.previous
                      ? {}
                      : { title: item?.previous })}
                  >
                    {item?.previous || EMPTY_FIELD_VALUE}
                  </EllipsisText>
                </Box>
                <Box
                  width="37%"
                  pad={{ left: '36px' }}
                  justify="center"
                  style={{ position: 'relative' }}
                  data-testid={`ActivityLog_changeset_${id}_row_${item.id}_updated`}
                >
                  <ChevronRightIcon
                    size="8px"
                    color="iconSecondary"
                    style={{ position: 'absolute', marginLeft: '-20px' }}
                    data-testid={`ActivityLog_changeset_${id}_row_${item.id}_arrow`}
                  />
                  {item?.updatedNode ? (
                    item.updatedNode
                  ) : (
                    <EllipsisText
                      color="textBody"
                      weight="bold"
                      hasUppercase={false}
                      data-testid={`ActivityLog_changeset_${id}_row_${item.id}_updated-value`}
                      {...(item?.updated === EMPTY_FIELD_VALUE || !item?.updated
                        ? {}
                        : { title: item?.updated })}
                    >
                      {item?.updated || EMPTY_FIELD_VALUE}
                    </EllipsisText>
                  )}
                </Box>
              </>
            )}
          </Box>
        );
      })}
    </CahngesetWrapper>
  );
};

export default Body;
